import Minze, { EventListeners, MinzeElement, Reactive } from 'minze'
import { IResult, ISearchConfig, MultipleResults } from '../../../../modules/search'
import { SearchResultsSubscriber } from '../../../../modules/search/SearchResultsSubscriber'
import { UrlToSearchSubscriber } from '../../../../modules/search/UrlToSearchSubscriber'
import MainModule from '../../../../modules/main'
import { compileSearchTabItems } from './helpers'
import { renderPagination } from '../pagination/renderPagination'
import { SearchTabItem } from './lvc-search-results-tabs'
import { getCustomPartnerId } from '../../../../modules/routing'

// Think about order here, it seems to have to match the helper function
const MODAL_LIST = [
  'events_search',
  'exhibitions_search',
  'festivals_search',
  'eventseries_only',
  'places_full',
  'artists',
]

export interface LvcSearchResults {
  results: MultipleResults
  modal: string
  items: SearchTabItem[]
  initialTabIndex: number
  currentTabIndex: number
  isSearchModified: boolean
  isFreeFilterApplied: boolean
  isHasEventsFilterApplied: boolean
}

export class LvcSearchResults extends MinzeElement {
  reactive: Reactive = [
    ['results', null],
    ['modal', 'events_search'],
    ['isSearchModified', false],
    ['isFreeFilterApplied', false],
    ['isHasEventsFilterApplied', true],
  ]
  urlToSearchSubscriber: UrlToSearchSubscriber | null = null
  searchResultsSubscriber: SearchResultsSubscriber | null = null
  items: SearchTabItem[] = []
  // Listen for search results and update the results when they change

  onResultUpdateCallback(result: IResult | MultipleResults) {
    const isFacetApplied = (facetName: string) => {
      return !!this.urlToSearchSubscriber
        ?.getSearchConfig()
        ?.facets?.find(
          facet =>
            facet.name === facetName &&
            facet.options.find(option => option.selected && option.value === 'true')
        )
    }
    const isFreeFilterApplied = isFacetApplied('isAccessibleForFree')
    const isHasEventsFilterApplied = isFacetApplied('hasPublishedEvents')
    this.results = result as MultipleResults

    // Get items ready to be rendered by the tab component.
    this.items = compileSearchTabItems(this.results)

    //await delay(100); // Not sure this is needed. TBD

    // Setting the initial tab index to the current modal index (for initial loading of page)
    this.initialTabIndex = MODAL_LIST.indexOf(this.modal)

    const oldTabIndex = this.currentTabIndex
    if (this.isSearchModified && this.initialTabIndex !== oldTabIndex) {
      this.isSearchModified = true
      this.currentTabIndex = this.initialTabIndex
      return this.rerender()
    }

    // If there is only one tab with results, set the initialTabIndex to that tab
    const tabsWithResults = this.items.filter(item => {
      if (item?.total) return item.total > 0
    })
    if (
      tabsWithResults.length &&
      !tabsWithResults.find(item => item.modalName === this.modal) &&
      this.isFreeFilterApplied === isFreeFilterApplied &&
      this.isHasEventsFilterApplied === isHasEventsFilterApplied
    ) {
      this.initialTabIndex = MODAL_LIST.indexOf(tabsWithResults[0].modalName)
    } else {
      this.initialTabIndex = MODAL_LIST.indexOf(this.modal)
    }
    this.isSearchModified = true
    this.currentTabIndex = this.initialTabIndex
    this.isFreeFilterApplied = isFreeFilterApplied
    this.isHasEventsFilterApplied = isHasEventsFilterApplied

    this.rerender(true)
  }

  // Listen for search config changes and update the modal when it changes
  onSearchConfigCallback = (searchConfig: ISearchConfig) => {
    searchConfig.modal && (this.modal = searchConfig.modal)
  }

  onSearchResultsSubscriberReady = async () => {
    const mainModule = MainModule.getInstance()
    this.urlToSearchSubscriber = new UrlToSearchSubscriber(this.dataGroupId as string)
    await mainModule.getSearchManager().subscribe(this.urlToSearchSubscriber)
  }

  onReady = async () => {
    this.searchResultsSubscriber = new SearchResultsSubscriber(
      this.dataGroupId as string,
      this.onResultUpdateCallback.bind(this),
      this.onSearchConfigCallback.bind(this),
      this.onSearchResultsSubscriberReady.bind(this)
    )
    await MainModule.getInstance().getSearchManager().subscribe(this.searchResultsSubscriber)

    // Treat this as a way to initialize recommendations, only for "search-results component"
    // Check if _recommendedContent is present, if not attempt a fresh fetch from storage.
    if (!MainModule.getInstance().getRecommendedContent()) {
      await MainModule.getInstance().setRecommendedContent()
    }

    this.rerender()
  }

  onRender = () => {
    // Re-render pagination on each render.
    if (!this.results?.[this.modal]?.total) return
    const currentPage =
      this.searchResultsSubscriber?.core?.searchConfig?.options?.page[`${this.modal}`] || 0
    const totalPages = this.results?.[this.modal]?.numberOfPages
    const paginationWrapper = this.select('.pagination__list') as HTMLElement
    if (!paginationWrapper) return
    renderPagination(paginationWrapper, currentPage + 1, totalPages) // +1 because pages are 0-indexeds from Algolia
  }

  handleOnTabClick = async (e: CustomEvent) => {
    if (e.type !== 'lvc:search-tab-clicked') return
    if (!e?.detail?.modalName) return
    this.modal = e.detail.modalName

    // Reset page to 0 before changing tabs
    await this.searchResultsSubscriber?.resetPage(this.modal)

    // Switch to new tab and load results for that modalIndex
    await this.searchResultsSubscriber?.openModal(this.modal)
  }

  handlePaginationOnChange = async (e: CustomEvent) => {
    if (e.type !== 'lvc:search-pagination-event') return
    if (!e?.detail?.goToPage) return

    const modal = this.modal
    const page = e.detail.goToPage - 1 // -1 because pages are 0-indexeds from Algolia

    // Change page for the current modalIndex
    await this.searchResultsSubscriber?.changePage(modal, page)
  }

  html = () => `
    ${
      this.items && this.initialTabIndex >= 0
        ? /*html*/ `
      <lvc-search-results-text results="${encodeURIComponent(JSON.stringify(this.items))}"></lvc-search-results-text>
      <lvc-search-results-tabs items="${encodeURIComponent(JSON.stringify(this.items))}" initial-tab-index="${this.initialTabIndex}"></lvc-search-results-tabs>
    `
        : ''
    }
    <div class="pagination__list"></div>
  `
  css = () => `
    :host {
      width: 100%;
      max-width: 80rem;
      margin: 0 auto;
    }
    .container-large {
      max-width: 80rem;
      margin: 0 auto;
      width: 100%
    }
    .padding-global {
      padding-left: 6rem;
      padding-right: 6rem;
    }
    lvc-search-results-tabs {
      margin-top: 3rem;
    }
    .counter {
      font-size: 14px;
      margin-top: -14px;
    }
    .pagination__list {
    display: flex;
    gap: 10px;
    justify-content: center;
    margin-top: 4rem;
    }
    .pagination__prev, .pagination__next, .pagination__first, .pagination__last, .pagination__page, .ellipsis {
      font-family: inherit;
      font-size: 1.5rem;
      font-weight: 500;
      background: none;
      color: #1E007D;
      border: none;
      cursor: pointer;
      width: 40px;
      height: 40px;
      border-radius: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .pagination__page.is-active {
      background: #DCFFD7;
    }
    .ellipsis {
      cursor: default;
    }
    .pagination__wrapper {
      margin-top: 10rem;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 16px;
    }
    @media screen and (max-width: 991px) {
      .pagination__list {
        margin-bottom: 7rem;
        font-size: 14px;
      }
      .pagination__prev, .pagination__next, .pagination__first, .pagination__last, .pagination__page, .ellipsis {
        font-size: 18px;
        width: 30px;
        height: 30px;
      }
      .pagination__icon {
        width: 16px;
        height: 16px;
      }
    }
  `

  eventListeners: EventListeners = [
    [window, 'lvc:search-tab-clicked', e => this.handleOnTabClick(e as CustomEvent)],
    [window, 'lvc:search-pagination-event', e => this.handlePaginationOnChange(e as CustomEvent)],
  ]
}
